import React, { useState, useEffect } from 'react';
import { Modal, FormItem, message } from '.';
import { versionApi } from '../../api';
import { useAuth } from '../../context/AuthContext';

const VersionModal = ({ visible, onClose }) => {
    const { user } = useAuth();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPublishing, setIsPublishing] = useState(false);
    const [formData, setFormData] = useState({ version_number: '', title: '', content: '' });

    const fetchVersions = async () => {
        setLoading(true);
        try {
            const res = await versionApi.getList();
            setVersions(res.data);
        } catch (err) {
            message.error('获取版本信息失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && !isPublishing) {
            fetchVersions();
        }
    }, [visible, isPublishing]);

    const handlePublish = async () => {
        if (!formData.version_number || !formData.title) {
            message.error('请填写版本号和标题');
            return;
        }
        try {
            await versionApi.create(formData);
            message.success('发布成功');
            setIsPublishing(false);
            setFormData({ version_number: '', title: '', content: '' });
            fetchVersions();
        } catch (err) {
            message.error(err.message);
        }
    };

    const getNextVersion = (latestVersion) => {
        if (!latestVersion) return 'v1.0.0';
        const match = latestVersion.match(/^(.*?)(\d+)$/);
        if (match) {
            const prefix = match[1];
            const number = parseInt(match[2]);
            return `${prefix}${number + 1}`;
        }
        return latestVersion;
    };

    const handleOpenPublish = () => {
        let nextVersion = 'v1.0.0';
        if (versions.length > 0) {
            const latest = versions[0].version_number;
            nextVersion = getNextVersion(latest);
            if (nextVersion === latest) {
                message.info('无法自动递增版本号，已填充上一版本号，请修改');
            }
        }
        setFormData(prev => ({ ...prev, version_number: nextVersion }));
        setIsPublishing(true);
    };

    const isAdmin = user && user.role === 'admin';

    if (isPublishing) {
        return (
            <Modal
                visible={visible}
                title="发布新版本"
                onClose={() => setIsPublishing(false)} // 返回列表而不是关闭模态框
                footer={
                    <>
                        <button className="btn btn-default" onClick={() => setIsPublishing(false)}>返回列表</button>
                        <button className="btn btn-primary" onClick={handlePublish}>发布</button>
                    </>
                }
            >
                <FormItem label="版本号" required>
                    {versions.length > 0 && (
                        <span style={{ fontSize: '12px', color: '#999', float: 'right' }}>
                            最新版本: {versions[0].version_number}
                        </span>
                    )}
                    <input
                        placeholder="例如 v1.0.1"
                        value={formData.version_number}
                        onChange={e => setFormData({ ...formData, version_number: e.target.value })}
                    />
                </FormItem>
                <FormItem label="标题" required>
                    <input
                        placeholder="本次更新的简要标题"
                        value={formData.title}
                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                    />
                </FormItem>
                <FormItem label="更新内容">
                    <textarea
                        rows={6}
                        placeholder="详细的更新内容..."
                        value={formData.content}
                        onChange={e => setFormData({ ...formData, content: e.target.value })}
                    />
                </FormItem>
            </Modal>
        );
    }

    return (
        <Modal
            visible={visible}
            title="系统版本历史"
            onClose={onClose}
            footer={
                isAdmin ? (
                    <button className="btn btn-primary" onClick={handleOpenPublish}>
                        发布新版本
                    </button>
                ) : (
                    <button className="btn btn-default" onClick={onClose}>
                        关闭
                    </button>
                )
            }
        >
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {loading ? (
                    <p style={{ textAlign: 'center', color: '#999' }}>加载中...</p>
                ) : versions.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#999' }}>暂无版本信息</p>
                ) : (
                    versions.map((v, index) => (
                        <div key={v.id} style={{
                            marginBottom: '20px',
                            paddingBottom: '20px',
                            borderBottom: index !== versions.length - 1 ? '1px solid #eee' : 'none'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3 style={{ margin: 0, color: '#1890ff' }}>
                                    {v.version_number} <span style={{ color: '#333', fontWeight: 'normal', fontSize: '14px' }}>{v.title}</span>
                                </h3>
                                <span style={{ fontSize: '12px', color: '#999' }}>
                                    {new Date(v.publish_time).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}
                                </span>
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', color: '#666', lineHeight: '1.6', fontSize: '14px' }}>
                                {v.content || '无详细内容'}
                            </div>
                            {v.publisher_name && (
                                <div style={{ marginTop: '5px', fontSize: '12px', color: '#ccc', textAlign: 'right' }}>
                                    发布人: {v.publisher_name}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </Modal>
    );
};

export default VersionModal;
